import { BaseComponentProps } from '../types';
import useAuth from '../hooks/context/useAuth';
import useStore from '../hooks/context/useStore';
import useOngoingOrder from '../hooks/context/useOngoingOrder';
import { loadCustomerDetails } from '../services/api/CustomerService';
import { StoreDetails } from '../types/store';
import useNotification from '../hooks/context/useNotification';
import useForceRender from '../hooks/context/useForceRender';
import { ReservedKeys } from './ForceRenderProvider';
import { useEffect, useState } from 'react';
import { Routes } from '../routing/Router';
import { useNavigate } from 'react-router';
import { ICustomerDetails } from '../types/customer';
import useCustomerDetails from '../hooks/context/useCustomerDetails';
import { IVoximplantCall } from '../types/external/Voximplant';
import { ICall } from '../types/call';
import CallHelpers from '../services/utils/call/CallHelpers';
import useDeepRef from '../hooks/utility/useDeepRef';

// export type OngoingCallContextType = {};

// export const OngoingCallContext = React.createContext<OngoingCallContextType>(null);

export default function OngoingCallProvider({ children }: BaseComponentProps) {
  const { user } = useAuth();
  const { setCustomerData } = useCustomerDetails();
  const { loadStoreData, setShowSidebar } = useStore();
  const { orderData, updateOrderData, resetOrderData } = useOngoingOrder();
  const navigate = useNavigate();

  const { notificationApi } = useNotification();
  const { forceRender } = useForceRender();

  const [ongoingCallId, setOngoingCallId] = useState<string | null>(null);
  const ongoingCallIdRef = useDeepRef(ongoingCallId);

  useEffect(() => {
    if (!user) {
      return;
    }

    window.VoximplantKitSettings = {
      visualSettings: {
        drawMode: 'full',
        side: 'left',
        cssSelector: '#vox-kit-softphone',
      },
      authSettings: {
        type: 'auth-service',
        api_url: 'kit-eu.voximplant.com',
      },
    };

    const settings = window.VoximplantKitSettings;
    if (settings && typeof window.VoximplantKit === 'object') {
      window.VoximplantKit.init(settings);
    }
  }, [user]);

  useEffect(() => {
    if (typeof window.VoximplantKit?.Call === 'object') {
      window.VoximplantKit.Call.on(
        'DataUpdated',
        ({ CALL }: { CALL: IVoximplantCall }) =>
          CALL.incoming && handleInboundCall(CallHelpers.fromVoximplan(CALL)),
      );
      window.VoximplantKit.Call.on(
        'CallEnded',
        (call: IVoximplantCall) => call.id === ongoingCallId && setOngoingCallId(null),
      );
    }
  }, [typeof window.VoximplantKit?.Call]);

  function handleInboundCall(call: ICall) {
    const initOrder = (storeData: StoreDetails) => {
      if (!storeData?.id) {
        throw new Error('Receiving a call for an unknown store');
      }

      resetOrderData();
      setShowSidebar(true);

      const _loadCustomerDetails = async () => {
        return loadCustomerDetails(storeData.id, { phone_number: call.caller })
          .then(({ data }) => data)
          .catch(() => null);
      };

      const updateCustomerInfo = (customer: null | ICustomerDetails) => {
        setCustomerData(customer);

        if (!customer) {
          return updateOrderData({
            call_id: call.id,
            customer: {
              ...orderData.customer,
              phone_number: call.caller,
            },
          });
        }

        updateOrderData({
          call_id: call.id,
          customer,
          delivery_info: {
            ...customer.delivery_info,
            zone: null,
            quote: null,
          },
        });
      };

      return _loadCustomerDetails()
        .then((customer) => updateCustomerInfo(customer))
        .then(() => forceRender(ReservedKeys.CustomerPhoneNumber))
        .then(() => navigate(`/${Routes.NewOrder.Root}`));
    };

    if (!call.is_incoming || call.id === ongoingCallIdRef.current) {
      return;
    }

    setOngoingCallId(call.id);
    loadStoreData({ occPhoneNumber: call.recipient })
      .then((storeData) => initOrder(storeData))
      .catch((error) => notificationApi.error({ message: error.message }));
  }

  return children;
}
